import lendAndBorrowTransactionHistoryAtom from '../lendAndBorrow/lendAndBorrowTransactionHistory';
import { avnuSwapTransactionHistoryAtom, nostraSwapTransactionHistoryAtom } from '../swap';
import poolsTransactionHistoryAtom from '../pool/poolTransactionHistoryAtom';
import { PoolToken, Token, TransactionHistoryItemType } from '../../interfaces';
import { addAddressPadding } from 'starknet';
import { getConfigManager } from '../../config';
import { DecimalFormat } from '@tempusfinance/decimal';
import { avnuSupportedTokensAtom } from '../swap';
import { Decimal } from '../../datastructures';
import { createAsyncAtom } from '../../lib/state';
import { AvnuSwapTransactionHistoryItem } from '../../interfaces/AvnuSwapTransactionHistoryItem';
import bridgeHistoryAtom from '../bridge/bridgeHistoryAtom';
import { walletAddressAtom } from '../wallet';
import { activeAccountIdAtom } from '../lendAndBorrow/activeAccountIdAtom';
import { getAccountAddressFromId } from '../../utils';

export interface TransactionHistoryItem {
  tokens?: (Token | PoolToken)[];
  externalTokens?: string[];
  message: string;
  txHash?: string;
  txnExplorerUrls?: { url: string; label: string }[];
  timestamp: number;
  type: TransactionHistoryItemType;
}

const transactionHistoryAtom = createAsyncAtom(async get => {
  const lendAndBorrowTransactionHistory = await get(lendAndBorrowTransactionHistoryAtom);
  const avnuSwapTransactionHistory = await get(avnuSwapTransactionHistoryAtom);
  const nostraSwapTransactionHistory = await get(nostraSwapTransactionHistoryAtom);
  const poolTransactionHistory = await get(poolsTransactionHistoryAtom);
  const bridgeHistory = await get(bridgeHistoryAtom);
  const avnuSupportedTokens = await get(avnuSupportedTokensAtom);
  const walletAddress = await get(walletAddressAtom);
  const activeAccountId = await get(activeAccountIdAtom);

  const activeAccountAddress = walletAddress
    ? addAddressPadding(getAccountAddressFromId(walletAddress, activeAccountId)).toLowerCase()
    : null;

  const transactionHistory: TransactionHistoryItem[] = [];

  if (lendAndBorrowTransactionHistory) {
    lendAndBorrowTransactionHistory.forEach(transaction => {
      const configManager = getConfigManager();

      let assetAddress: string | null = null;
      if (transaction.type !== 'ADJUST') {
        assetAddress = configManager.getNostraTokenAssetAddress(transaction.tokens.tokenAddress);
      } else {
        assetAddress = configManager.getNostraTokenAssetAddress(transaction.tokens.toTokenAddress);
      }

      if (assetAddress === null) {
        console.error(`Failed to find asset address for transaction ${transaction.hash}`);
        return null;
      }

      const asset = configManager.getAssetByAddress(assetAddress);
      if (!asset) {
        return;
      }

      const amountFormatted = DecimalFormat.format(transaction.amount, {
        style: 'currency',
        currency: asset.ticker,
        fractionDigits: asset.uiTokenPrecision,
        pad: true,
        lessThanFormat: true,
      });

      transactionHistory.push({
        tokens: [asset.ticker],
        message: `${capitalizeFirstLetter(transaction.type.toLowerCase())} ${amountFormatted}`,
        txHash: transaction.hash,
        timestamp: transaction.date.getTime(),
        type: transaction.type,
      });
    });
  }

  if (avnuSwapTransactionHistory || nostraSwapTransactionHistory) {
    const allSwapTransactionHistory = [...(avnuSwapTransactionHistory ?? []), ...(nostraSwapTransactionHistory ?? [])];
    const swapTransactionHistoryMap: Record<string, AvnuSwapTransactionHistoryItem> = {};
    allSwapTransactionHistory.forEach(transaction => {
      if (!swapTransactionHistoryMap[transaction.hash]) {
        swapTransactionHistoryMap[transaction.hash] = transaction;
      }
    });
    const sortedSwapTransactionHistory = Object.values(swapTransactionHistoryMap)
      .filter(transaction => {
        if (!activeAccountAddress || !transaction.account) return true;
        return addAddressPadding(transaction.account).toLowerCase() === activeAccountAddress;
      })
      .sort((a, b) => b.timestamp - a.timestamp);
    sortedSwapTransactionHistory.forEach(transaction => {
      const sellTokenSymbol = Object.keys(avnuSupportedTokens).find(key => {
        const configAddress = addAddressPadding(avnuSupportedTokens[key].address).toLowerCase();
        const transactionAddress = addAddressPadding(transaction.sellAddress).toLowerCase();

        return configAddress === transactionAddress;
      });

      if (!sellTokenSymbol) {
        return null;
      }

      const sellTokenConfig = avnuSupportedTokens[sellTokenSymbol];

      const buyTokenSymbol = Object.keys(avnuSupportedTokens).find(key => {
        const configAddress = addAddressPadding(avnuSupportedTokens[key].address).toLowerCase();
        const transactionAddress = addAddressPadding(transaction.buyAddress).toLowerCase();

        return configAddress === transactionAddress;
      });
      if (!buyTokenSymbol) {
        return null;
      }

      const buyTokenConfig = avnuSupportedTokens[buyTokenSymbol];

      const buyAmountFormatted = DecimalFormat.format(
        new Decimal(BigInt(transaction.buyAmount), buyTokenConfig.decimals),
        {
          style: 'currency',
          currency: buyTokenConfig.symbol,
          fractionDigits: 4,
          pad: true,
          lessThanFormat: true,
        },
      );

      const sellAmountFormatted = DecimalFormat.format(
        new Decimal(BigInt(transaction.sellAmount), sellTokenConfig.decimals),
        {
          style: 'currency',
          currency: sellTokenConfig.symbol,
          fractionDigits: 4,
          pad: true,
          lessThanFormat: true,
        },
      );

      transactionHistory.push({
        tokens: [sellTokenConfig.symbol as PoolToken, buyTokenConfig.symbol as PoolToken],
        message: `Swap ${sellAmountFormatted} for ${buyAmountFormatted}`,
        txHash: transaction.hash,
        timestamp: transaction.timestamp,
        type: 'SWAP',
      });
    });
  }

  if (poolTransactionHistory) {
    poolTransactionHistory
      .filter(transaction => {
        if (!activeAccountAddress || !transaction.account) return true;
        return addAddressPadding(transaction.account).toLowerCase() === activeAccountAddress;
      })
      .forEach(transaction => {
      const tokenA = transaction.poolPair.tokenA.ticker;
      const tokenB = transaction.poolPair.tokenB.ticker;

      const tokenAAmountFormatted = DecimalFormat.format(transaction.tokenAAmount, {
        style: 'currency',
        currency: tokenA,
        fractionDigits: transaction.poolPair.tokenA.uiDecimals,
        pad: true,
        lessThanFormat: true,
      });

      const tokenBAmountFormatted = DecimalFormat.format(transaction.tokenBAmount, {
        style: 'currency',
        currency: tokenB,
        fractionDigits: transaction.poolPair.tokenB.uiDecimals,
        pad: true,
        lessThanFormat: true,
      });

      transactionHistory.push({
        tokens: [tokenA as PoolToken, tokenB as PoolToken],
        message: `Pool ${
          transaction.type === 'deposit' ? 'Deposit' : 'Withdraw'
        } ${tokenAAmountFormatted} and ${tokenBAmountFormatted}`,
        txHash: transaction.hash,
        timestamp: transaction.date.getTime(),
        type: transaction.type === 'deposit' ? 'DEPOSIT' : 'WITHDRAW',
      });
    });
  }

  if (bridgeHistory) {
    bridgeHistory.forEach(history => {
      transactionHistory.push({
        externalTokens: [history.transferTransaction.token.logo],
        message: `Bridge ${history.transferTransaction.amount.toTruncated(4)} ${
          history.transferTransaction.token.symbol
        } from ${history.transferTransaction.network.displayName} to ${history.bridgeTransaction.network.displayName}`,
        txnExplorerUrls: [
          {
            label: 'Deposit',
            url: history.transferTransaction.network.transactionExplorerTemplate.replace(
              '{0}',
              history.transferTransaction.hash ?? '',
            ),
          },
          {
            label: 'Fill',
            url: history.bridgeTransaction.network.transactionExplorerTemplate.replace(
              '{0}',
              history.bridgeTransaction.hash ?? '',
            ),
          },
        ],
        timestamp: history.transferTransaction.date.getTime(),
        type: 'BRIDGE',
      });
    });
  }

  return transactionHistory.sort((a, b) => b.timestamp - a.timestamp);
});

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export default transactionHistoryAtom;
